/**
 * World-render colour constants (HEX-100). These compose the overworld hex
 * faces, glyphs and fog — the values `faceColour` used to hold inline. They live
 * here so `faceColour.ts` carries no bare hex (the theme is the one source).
 *
 * Biome anchors are pulled from the canonical Moss & Bone token set
 * ([[tokens]] `BIOME_ANCHOR`); everything else here is the existing schematic
 * world palette, relocated unchanged. ADR-0020 §5 hands the painterly World View
 * (and any re-pick of these tile/fog colours) to the ADR-0018 worldgen/compass
 * wave — this module just centralises today's values so that wave has one place
 * to evolve them.
 */

import type { ColourHex } from './tokens';

/**
 * Per-type tile face colour. Not an ADR-0020 token (the handoff defers tile
 * colours to the World View redesign, ADR-0020 §5) — these are the current
 * values, named. `dangerous` mirrors `ground` (HEX-85: a hidden trap is
 * indistinguishable from ground until sprung).
 */
export const WORLD_TYPE_COLOUR = {
  ground: 0x4a6b3a,
  mountain: 0x6e655c,
  dangerous: 0x4a6b3a,
  pillar: 0x2fb6c0,
  road: 0x6b5a3a,
  shop: 0x9f7a3e,
  town: 0x8a6a36,
  encounter: 0xc25a1f,
  chest: 0xc6a23f,
  'traveller-merchant': 0x7a3aa8,
} as const;

/** The `?` card-back face for undiscovered-in-vision tiles (ADR-0014 §1, §6). */
export const FRONTIER_SLATE: ColourHex = 0x2a2a36;
/** The `?` glyph fill drawn on top of the frontier slate face (ADR-0014 §6). */
export const FRONTIER_GLYPH: ColourHex = 0x6a6a82;

/** Tile-type glyph fill / outline (HEX-66) — reads against every biome tint. */
export const TILE_GLYPH_FILL: ColourHex = 0xeef1f4;
export const TILE_GLYPH_STROKE: ColourHex = 0x14141a;

/** Memory-mode blend target — slightly darker than the page background (ADR-0014 §6). */
export const MEMORY_BLEND_TARGET: ColourHex = 0x1a1a22;

/**
 * Distant-mountain silhouette haze (HEX-127). A landmark mountain — one beyond
 * the vision/memory radius but within the skyline scan — blends toward this cool
 * slate, giving it the flat blue cast of an atmospheric ridge so it reads as
 * "far off, steer by it" and stays visibly distinct from the neutral memory dim.
 */
export const LANDMARK_HAZE: ColourHex = 0x2c3a52;

/** Sprung-trap warning tint (HEX-96). */
export const DANGER_TINT: ColourHex = 0xc23a2a;

/** Hex outline drawn on every cell; the hover variant is one step lighter (ADR-0014 §5). */
export const HEX_OUTLINE_COLOUR: ColourHex = 0x14141a;
export const HEX_OUTLINE_HOVER_COLOUR: ColourHex = 0x3a3a48;
